import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import AudioPlayer from '../components/AudioPlayer';
import { contentService } from '../services/firebaseService';
import { HomeContent } from '../types';

// Feature flag to show/hide audio players
const SHOW_AUDIO_PLAYERS = false;

const HomePage: React.FC = () => {
  const [homeContent, setHomeContent] = useState<HomeContent | null>(null);
  // Start with loading false so default content shows immediately
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadHomeContent();
  }, []);

  const loadHomeContent = async () => {
    // Try to load from Firebase with timeout (non-blocking)
    try {
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout')), 10000)
      );
      
      const contentPromise = contentService.getHomeContent();
      const content = await Promise.race([contentPromise, timeoutPromise]) as HomeContent | null;
      
      if (content) {
        console.log('🏠 Loaded home content from Firestore:', {
          mission: content.mission?.substring(0, 50) + '...',
          missionAudioUrl: content.missionAudioUrl,
          visionAudioUrl: content.visionAudioUrl,
          getInvolvedAudioUrl: content.getInvolvedAudioUrl
        });
        setHomeContent(content);
      } else {
        console.log('⚠️ No home content found in Firestore, using defaults');
      }
    } catch (error: any) {
      if (error?.message === 'Timeout') {
        console.warn('⏱️ Timeout loading home content from Firestore (10s). Using default content. Audio players may not appear if audio URLs exist in Firestore.');
      } else {
        console.error('❌ Error loading home content from Firestore:', error);
        console.error('Error details:', {
          message: error?.message,
          code: error?.code,
          stack: error?.stack
        });
      }
      // Use default content - already set as fallback
    }
  };

  // Default content if Firebase doesn't have it yet
  const defaultContent: HomeContent = {
    mission: 'To foster compassion and understanding through educational programs and community initiatives that bring people together.',
    vision: 'A world where compassion guides our interactions and creates positive change in communities everywhere.',
    getInvolved: 'Join us in making a difference. Whether through volunteering, donating, or participating in our programs.'
  };

  const displayContent = homeContent || defaultContent;
  
  // Debug: Log what content is being displayed
  useEffect(() => {
    console.log('🎵 Current display content:', {
      hasHomeContent: !!homeContent,
      missionAudioUrl: displayContent.missionAudioUrl,
      visionAudioUrl: displayContent.visionAudioUrl,
      getInvolvedAudioUrl: displayContent.getInvolvedAudioUrl
    });
  }, [displayContent, homeContent]);

  return (
    <Layout>
      <section className="hero">
        <div className="container">
          <img 
            src="/Logo_TM.png" 
            alt="Compassion Course Foundation Logo" 
            className="hero-logo"
            loading="eager"
            decoding="async"
            fetchpriority="high"
          />
          <p>Peace Education for Everyone, Everywhere, All at Once.</p>
          <div className="hero-actions">
            <a href="/about" className="btn btn-primary">About Us</a>
            <a href="/donate" className="btn btn-primary btn-donate-hero">Donate</a>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="grid">
            <div 
              className="card" 
            >
              <h3>Our Mission</h3>
              {loading ? (
                <p>Loading...</p>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {displayContent.mission?.split('\n').map((paragraph, index) => (
                    <p key={index} style={{ marginBottom: paragraph.trim() ? '0.5rem' : '0' }}>
                      {paragraph || '\u00A0'}
                    </p>
                  ))}
                </div>
              )}
              {SHOW_AUDIO_PLAYERS && displayContent.missionAudioUrl && typeof displayContent.missionAudioUrl === 'string' && displayContent.missionAudioUrl.trim() !== '' && (
                <div style={{ marginTop: '1rem' }}>
                  <AudioPlayer src={displayContent.missionAudioUrl} />
                </div>
              )}
            </div>
            <div className="card">
              <h3>Our Vision</h3>
              {loading ? (
                <p>Loading...</p>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {displayContent.vision?.split('\n').map((paragraph, index) => (
                    <p key={index} style={{ marginBottom: paragraph.trim() ? '0.5rem' : '0' }}>
                      {paragraph || '\u00A0'}
                    </p>
                  ))}
                </div>
              )}
              {SHOW_AUDIO_PLAYERS && displayContent.visionAudioUrl && (
                <div style={{ marginTop: '1rem' }}>
                  <AudioPlayer src={displayContent.visionAudioUrl} />
                </div>
              )}
            </div>
            <div className="card">
              <h3>Get Involved</h3>
              {loading ? (
                <p>Loading...</p>
              ) : (
                <div style={{ whiteSpace: 'pre-wrap' }}>
                  {displayContent.getInvolved?.split('\n').map((paragraph, index) => (
                    <p key={index} style={{ marginBottom: paragraph.trim() ? '0.5rem' : '0' }}>
                      {paragraph || '\u00A0'}
                    </p>
                  ))}
                </div>
              )}
              {SHOW_AUDIO_PLAYERS && displayContent.getInvolvedAudioUrl && (
                <div style={{ marginTop: '1rem' }}>
                  <AudioPlayer src={displayContent.getInvolvedAudioUrl} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
